// WebSocket 메시지 계약
//
// **서버는 절대 시각 앵커만 보내고 카운트다운 틱을 보내지 않는다.** 이 규율이 이
// 제품의 정확도가 서 있는 자리다. 틱을 보내면 화면의 숫자가 네트워크 지터를 그대로
// 물려받아, 지연이 200밀리초 튀는 순간 모든 사람의 카운트다운이 함께 튄다. 앵커만
// 보내면 각 브라우저가 자기 시계 동기 위에서 스스로 세므로 지터가 숫자에 실리지 않는다.
//
// 그래서 이 목록에 `tick` 류 메시지가 없는 것이 계약이다. 서버에서 setInterval 로
// 시각을 밀어내는 코드는 lint 가 막고, 여기 틱 메시지가 생기는 것은 계약 스냅샷이
// 커밋 전에 드러낸다.

import { Type, type Static } from '@sinclair/typebox'

import * as S from './schema.ts'

/** 서버 → 브라우저. 전부 "지금 상태가 이렇다" 이고, "지금 몇 초 남았다" 는 없다. */
export const ServerMessage = Type.Union([
  Type.Object({ kind: Type.Literal('room.state'), room: S.RoomPublic }),
  Type.Object({ kind: Type.Literal('sequence.updated'), sequence: S.Sequence }),
  Type.Object({
    kind: Type.Literal('presence.updated'),
    entries: Type.Array(S.PresenceEntry),
  }),
  Type.Object({ kind: Type.Literal('rosters.updated'), rosters: Type.Array(S.Roster) }),
  Type.Object({
    kind: Type.Literal('defense.updated'),
    enemyRallies: Type.Array(S.EnemyRally),
  }),
  // 유휴 초기화가 일어났음을 알린다. 무엇이 지워졌는지는 뒤따르는 상태 메시지가 말한다.
  Type.Object({ kind: Type.Literal('room.reset') }),
])

/** 브라우저 → 서버. heartbeat 는 접속 명단의 정확도를 지키는 유일한 자동 층이다. */
export const ClientMessage = Type.Union([
  Type.Object({ kind: Type.Literal('heartbeat') }),
  Type.Object({ kind: Type.Literal('subscribe'), sessionToken: S.Id }),
])

export type ServerMessage = Static<typeof ServerMessage>
export type ClientMessage = Static<typeof ClientMessage>

export const serverMessageKinds = [
  'room.state',
  'sequence.updated',
  'presence.updated',
  'rosters.updated',
  'defense.updated',
  'room.reset',
] as const

export const clientMessageKinds = ['heartbeat', 'subscribe'] as const

/**
 * 계약에 들어오면 안 되는 메시지 이름. 카운트다운을 서버가 밀어내는 형태로 되돌리는
 * 변경을 스냅샷 테스트가 이 목록으로 잡는다.
 */
export const FORBIDDEN_MESSAGE_FRAGMENTS = ['tick', 'countdown', 'remaining'] as const

/**
 * **서버가 「이 세션을 모른다」고 알리는 닫힘 코드.** 4000번대는 응용이 쓰라고 비워 둔
 * 자리다.
 *
 * 메시지가 아니라 닫힘 코드인 이유: 세션이 없으면 실어 보낼 채널 자체가 성립하지
 * 않는다. 인증되지 않은 소켓에 메시지를 보내면 "이 소켓이 무엇을 받을 자격이 있는가"를
 * 판단하는 자리가 하나 더 생긴다.
 *
 * **여기 사는 이유는 양쪽이 같은 값을 봐야 하기 때문이다.** 서버에만 두면 브라우저가
 * 숫자를 베껴 적게 되고, 베낀 숫자는 원본이 바뀔 때 따라오지 않는다. 그러면 화면은
 * 죽은 토큰으로 영원히 재접속을 시도하는 상태로 조용히 되돌아간다.
 *
 * **다만 계약 스냅샷이 이 값을 보지 않는다** — 스냅샷은 라우트 이름과 메시지 종류만
 * 담는다. 그래서 값이 갈리는 것을 잡는 자리가 단위 검증과 브라우저 검증 둘뿐이다.
 */
export const CLOSE_UNKNOWN_SESSION = 4401

/**
 * 서버가 세션을 **왜** 거뒀는가. 위 닫힘 코드의 이유 칸에 싣는다 (T4 개정 둘째, 2026-10-02 사용자).
 *
 * 코드 하나에 이유를 싣는 까닭은 화면이 할 일이 원인마다 달라서다. 명부에서 지워진 사람은 입장
 * 코드를 다시 넣어도 세 단계 뒤 「명단에 없는 게임 ID」에 막히므로 CT 에게 물어야 하고, 추방된
 * 사람은 명단에 그대로 있어 코드만 다시 넣으면 들어온다. 둘을 한 문장으로 말하면 지워진 사람에게
 * 막다른 길을 가리킨다(첫 대리 관측 둘이 같은 자리를 깎았다).
 *
 * **코드를 셋으로 늘리지 않는다.** 화면의 분류(다시 붙을지 말지)는 코드 하나로 이미 맞고, 이유는
 * 그 뒤에 무슨 문장을 띄울지만 가른다. 이유를 모르는 닫힘(구독 때 이미 없는 세션, 방을 다시 만든
 * 경우)은 「모름」으로 떨어진다.
 */
export const CLOSE_REASON_REMOVED = 'roster removed'
export const CLOSE_REASON_KICKED = 'kicked'
/**
 * 관리 코드 재발급이 거둔 CT 세션(2026-10-07 사용자 결정, 배포 전 보수 회차). 화면이 할 일이 추방과
 * 다르다. 끊긴 쪽은 입장 코드가 아니라 **새 관리 코드**를 받아야 다시 들어온다.
 */
export const CLOSE_REASON_ROTATED = 'admin code rotated'

export type SessionGoneCause = 'removed' | 'kicked' | 'rotated' | 'unknown'

/** 닫힘 이유 칸을 원인으로 읽는다. 모르는 글자는 「모름」이다. */
export function sessionGoneCause(reason: string): SessionGoneCause {
  if (reason === CLOSE_REASON_REMOVED) return 'removed'
  if (reason === CLOSE_REASON_KICKED) return 'kicked'
  if (reason === CLOSE_REASON_ROTATED) return 'rotated'
  return 'unknown'
}
